import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CheckIcon, XIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, StarIcon, ShieldIcon, ClockIcon, HeadphonesIcon, CalendarIcon, CreditCardIcon, GiftIcon } from 'lucide-react';
export function TravelClubPage() {
  const [activeTab, setActiveTab] = useState('benefits');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [selectedTier, setSelectedTier] = useState('gold');
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const testimonials = [{
    id: 1,
    name: 'Sarah Johnson',
    tier: 'Gold',
    image: 'https://randomuser.me/api/portraits/women/45.jpg',
    quote: 'The Gold membership has paid for itself multiple times over. The exclusive deals and priority support have made our family vacations stress-free.',
    trip: 'Swiss Alpine Adventure'
  }, {
    id: 2,
    name: 'Michael Chen',
    tier: 'Platinum',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    quote: 'As a frequent traveler, the Platinum tier benefits are invaluable. The dedicated concierge service has saved me countless hours of planning.',
    trip: 'Japan Cultural Tour'
  }, {
    id: 3,
    name: 'Emma Rodriguez',
    tier: 'Silver',
    image: 'https://randomuser.me/api/portraits/women/22.jpg',
    quote: 'Even the Silver membership offers great value. The member-only deals notification has helped us secure amazing trips at discounted rates.',
    trip: 'Italian Cuisine & Culture'
  }];
  const faqs = [{
    question: 'How do I join the EU Travel Club?',
    answer: 'Joining is simple. Select your preferred membership tier, complete the registration form, and make your payment securely online. Once your payment has been processed, you will receive a welcome email with your membership details and how to access your benefits immediately.'
  }, {
    question: 'Can I upgrade my membership tier later?',
    answer: "Yes, you can upgrade your membership tier at any time. When you upgrade, we will prorate the difference based on the remaining time of your current membership period. Simply log into your account, navigate to 'Membership Settings', and select 'Upgrade Membership'."
  }, {
    question: 'Are there any hidden fees or additional costs?',
    answer: 'No, there are no hidden fees. The annual membership fee covers all the benefits listed within your tier. While some premium services in the Platinum tier may have usage limits, such as airport lounge access, these are clearly indicated in the membership details.'
  }, {
    question: 'How do I apply my membership discount to bookings?',
    answer: "Your discount is automatically applied when you are logged into your account while making a booking. You'll see the discounted rates displayed throughout the booking process. If booking by phone, simply mention your membership number to our travel consultants."
  }, {
    question: 'Is my membership valid for international tours?',
    answer: 'Yes, your EU Travel Club membership is valid for all tours and packages offered by EU Holidays worldwide. The same discount percentages and benefits apply regardless of destination.'
  }, {
    question: 'Can I share my membership benefits with family members?',
    answer: "While the primary account holder must be present for bookings, your membership benefits extend to travel companions on the same booking. For family-specific options, we offer a 'Family Add-on' that can be purchased, to extend certain benefits to immediate family members, even when they are travelling separately."
  }];
  const exclusiveDeals = [{
    id: 1,
    title: 'Swiss Alpine Adventure',
    destination: 'Switzerland',
    regularPrice: 2500,
    memberPrice: 2125,
    discount: 15,
    image: 'https://images.unsplash.com/photo-1527668752968-14dc70a27c95?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&h=200&q=80',
    duration: '7 Days',
    featured: true
  }, {
    id: 2,
    title: 'Northern Lights Expedition',
    destination: 'Iceland',
    regularPrice: 3200,
    memberPrice: 2720,
    discount: 15,
    image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&h=200&q=80',
    duration: '6 Days',
    featured: false
  }, {
    id: 3,
    title: 'Tuscan Wine Tour',
    destination: 'Italy',
    regularPrice: 1800,
    memberPrice: 1530,
    discount: 15,
    image: 'https://images.unsplash.com/photo-1499678329028-101435549a4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&h=200&q=80',
    duration: '5 Days',
    featured: false
  }];
  return <div className="min-h-screen bg-white" data-id="element-2385">
      <Header data-id="element-2386" />
      {/* Hero Section */}
      <section className="relative" data-id="element-2387">
        <div className="absolute inset-0" data-id="element-2388">
          <img src="https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80" alt="Travel lifestyle" className="w-full h-full object-cover" data-id="element-2389" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" data-id="element-2390"></div>
        </div>
        <div className="relative container mx-auto px-4 py-24 md:py-32 lg:py-40" data-id="element-2391">
          <div className="max-w-3xl" data-id="element-2392">
            <div className="mb-6 flex space-x-2" data-id="element-2393">
              <span className="px-3 py-1 bg-gray-200 bg-opacity-20 backdrop-blur-sm text-white text-sm font-medium rounded-full" data-id="element-2394">
                Silver
              </span>
              <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 backdrop-blur-sm text-white text-sm font-medium rounded-full" data-id="element-2395">
                Gold
              </span>
              <span className="px-3 py-1 bg-gray-300 bg-opacity-20 backdrop-blur-sm text-white text-sm font-medium rounded-full" data-id="element-2396">
                Platinum
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-2397">
              EU Travel Club
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8" data-id="element-2398">
              Unlock Exclusive Benefits & Experiences
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-2399">
              <a href="#membership-tiers" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors text-center" data-id="element-2400">
                Join Today
              </a>
              <a href="#benefits" className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-md transition-colors backdrop-blur-sm text-center" data-id="element-2401">
                Explore Benefits
              </a>
            </div>
            <div className="mt-8 text-white/80 text-sm" data-id="element-2402">
              Join 15,000+ Elite Travelers enjoying premium benefits worldwide
            </div>
          </div>
        </div>
      </section>
      {/* Membership Tiers Section */}
      <section id="membership-tiers" className="py-16 bg-gray-50" data-id="element-2403">
        <div className="container mx-auto px-4" data-id="element-2404">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-2405">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2406">
              Choose Your Membership Level
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2407">
              Select the tier that best suits your travel style and frequency.
              All memberships renew annually.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2408">
            {/* Silver Tier */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md" data-id="element-2409">
              <div className="p-6 border-b border-gray-200" data-id="element-2410">
                <div className="flex justify-between items-center mb-4" data-id="element-2411">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-2412">
                    Silver Membership
                  </h3>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full" data-id="element-2413">
                    Basic Benefits
                  </span>
                </div>
                <div className="flex items-baseline mb-4" data-id="element-2414">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-2415">
                    RM 99
                  </span>
                  <span className="text-gray-600 ml-1" data-id="element-2416">/year</span>
                </div>
                <p className="text-gray-600 text-sm mb-6" data-id="element-2417">
                  Perfect for occasional travelers seeking value and special
                  offers.
                </p>
                <button className="w-full py-2 px-4 border border-blue-600 text-blue-600 font-medium rounded-md hover:bg-blue-50 transition-colors" onClick={() => setSelectedTier('silver')} data-id="element-2418">
                  Sign Up
                </button>
              </div>
              <div className="p-6" data-id="element-2419">
                <ul className="space-y-3" data-id="element-2420">
                  <li className="flex items-start" data-id="element-2421">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2422" />
                    <span className="text-gray-700" data-id="element-2423">
                      5% discount on all group tours
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2424">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2425" />
                    <span className="text-gray-700" data-id="element-2426">
                      Priority booking access
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2427">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2428" />
                    <span className="text-gray-700" data-id="element-2429">Quarterly newsletter</span>
                  </li>
                  <li className="flex items-start" data-id="element-2430">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2431" />
                    <span className="text-gray-700" data-id="element-2432">
                      Birthday month special offer
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2433">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2434" />
                    <span className="text-gray-700" data-id="element-2435">
                      Member-only deals notification
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            {/* Gold Tier */}
            <div className="bg-white rounded-xl shadow-md border-2 border-yellow-500 overflow-hidden transform scale-105 relative" data-id="element-2436">
              <div className="absolute top-0 right-0 left-0 bg-yellow-500 text-white text-center py-1 text-sm font-medium" data-id="element-2437">
                MOST POPULAR
              </div>
              <div className="p-6 border-b border-gray-200 pt-8" data-id="element-2438">
                <div className="flex justify-between items-center mb-4" data-id="element-2439">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-2440">
                    Gold Membership
                  </h3>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full" data-id="element-2441">
                    Best Value
                  </span>
                </div>
                <div className="flex items-baseline mb-4" data-id="element-2442">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-2443">
                    RM 199
                  </span>
                  <span className="text-gray-600 ml-1" data-id="element-2444">/year</span>
                </div>
                <p className="text-gray-600 text-sm mb-6" data-id="element-2445">
                  Ideal for regular travelers who want premium benefits and
                  savings.
                </p>
                <button className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors" onClick={() => setSelectedTier('gold')} data-id="element-2446">
                  Upgrade Now
                </button>
              </div>
              <div className="p-6" data-id="element-2447">
                <ul className="space-y-3" data-id="element-2448">
                  <li className="flex items-start" data-id="element-2449">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2450" />
                    <span className="text-gray-700" data-id="element-2451">
                      10% discount on all tours
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2452">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2453" />
                    <span className="text-gray-700" data-id="element-2454">15% on Flexi holidays</span>
                  </li>
                  <li className="flex items-start" data-id="element-2455">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2456" />
                    <span className="text-gray-700" data-id="element-2457">
                      Complimentary travel consultation
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2458">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2459" />
                    <span className="text-gray-700" data-id="element-2460">
                      Priority customer support
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2461">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2462" />
                    <span className="text-gray-700" data-id="element-2463">
                      Exclusive member events
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2464">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2465" />
                    <span className="text-gray-700" data-id="element-2466">
                      Travel insurance discount
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2467">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2468" />
                    <span className="text-gray-700" data-id="element-2469">
                      Airport lounge access (2 visits/year)
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            {/* Platinum Tier */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md" data-id="element-2470">
              <div className="p-6 border-b border-gray-200" data-id="element-2471">
                <div className="flex justify-between items-center mb-4" data-id="element-2472">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-2473">
                    Platinum Membership
                  </h3>
                  <span className="px-3 py-1 bg-gray-800 text-white text-xs font-medium rounded-full" data-id="element-2474">
                    Premium Experience
                  </span>
                </div>
                <div className="flex items-baseline mb-4" data-id="element-2475">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-2476">
                    RM 399
                  </span>
                  <span className="text-gray-600 ml-1" data-id="element-2477">/year</span>
                </div>
                <p className="text-gray-600 text-sm mb-6" data-id="element-2478">
                  Designed for frequent travelers seeking luxury and
                  personalized service.
                </p>
                <button className="w-full py-2 px-4 border border-blue-600 text-blue-600 font-medium rounded-md hover:bg-blue-50 transition-colors" onClick={() => setSelectedTier('platinum')} data-id="element-2479">
                  Go Platinum
                </button>
              </div>
              <div className="p-6" data-id="element-2480">
                <ul className="space-y-3" data-id="element-2481">
                  <li className="flex items-start" data-id="element-2482">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2483" />
                    <span className="text-gray-700" data-id="element-2484">
                      15% discount on all tours
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2485">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2486" />
                    <span className="text-gray-700" data-id="element-2487">
                      20% on luxury packages
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2488">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2489" />
                    <span className="text-gray-700" data-id="element-2490">
                      Dedicated travel concierge
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2491">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2492" />
                    <span className="text-gray-700" data-id="element-2493">
                      Unlimited travel consultations
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2494">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2495" />
                    <span className="text-gray-700" data-id="element-2496">
                      VIP member events & previews
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2497">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2498" />
                    <span className="text-gray-700" data-id="element-2499">
                      Complimentary travel insurance
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2500">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2501" />
                    <span className="text-gray-700" data-id="element-2502">
                      Airport lounge access (unlimited)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2503">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2504" />
                    <span className="text-gray-700" data-id="element-2505">
                      Room upgrades when available
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2506">
                    <CheckIcon size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2507" />
                    <span className="text-gray-700" data-id="element-2508">Welcome gift package</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Overview */}
      <section id="benefits" className="py-16" data-id="element-2509">
        <div className="container mx-auto px-4" data-id="element-2510">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-2511">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2512">
              What Our Members Enjoy
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2513">
              EU Travel Club membership opens the door to a world of exclusive
              privileges and benefits.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2514">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-2515">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-2516">
                <TagIcon size={24} className="text-blue-600" data-id="element-2517" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2518">
                Exclusive Discounts
              </h3>
              <p className="text-gray-700" data-id="element-2519">
                Save up to 20% on tours and packages worldwide. Members always
                get the best available rates.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-2520">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4" data-id="element-2521">
                <ClockIcon size={24} className="text-purple-600" data-id="element-2522" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2523">
                Priority Access
              </h3>
              <p className="text-gray-700" data-id="element-2524">
                Be the first to know about new destinations, tours, and
                limited-time offers before the general public.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-2525">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-2526">
                <HeadphonesIcon size={24} className="text-green-600" data-id="element-2527" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2528">
                Expert Support
              </h3>
              <p className="text-gray-700" data-id="element-2529">
                Access to dedicated travel consultants who can create
                personalized itineraries and answer all your questions.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-2530">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4" data-id="element-2531">
                <CalendarIcon size={24} className="text-amber-600" data-id="element-2532" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2533">
                Special Events
              </h3>
              <p className="text-gray-700" data-id="element-2534">
                Invitations to member-only travel events, workshops, and
                exclusive destination previews.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-2535">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4" data-id="element-2536">
                <ShieldIcon size={24} className="text-cyan-600" data-id="element-2537" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2538">
                Flexible Booking
              </h3>
              <p className="text-gray-700" data-id="element-2539">
                Enjoy enhanced cancellation policies and more flexibility with
                your travel plans as a valued member.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="element-2540">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-4" data-id="element-2541">
                <GiftIcon size={24} className="text-pink-600" data-id="element-2542" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2543">
                Bonus Rewards
              </h3>
              <p className="text-gray-700" data-id="element-2544">
                Earn points for every booking that can be redeemed for future
                travel, upgrades, or exclusive experiences.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 bg-gray-50" data-id="element-2545">
        <div className="container mx-auto px-4" data-id="element-2546">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-2547">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2548">
              What Our Members Say
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2549">
              Hear from travelers who have experienced the benefits of EU Travel
              Club membership.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2550">
            {testimonials.map(testimonial => <div key={testimonial.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-2551">
                <div className="flex items-center mb-4" data-id="element-2552">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-2553" />
                  <div data-id="element-2554">
                    <h4 className="font-medium text-gray-900" data-id="element-2555">
                      {testimonial.name}
                    </h4>
                    <div className="flex items-center" data-id="element-2556">
                      <span className={`text-xs px-2 py-0.5 rounded ${testimonial.tier === 'Silver' ? 'bg-gray-100 text-gray-600' : testimonial.tier === 'Gold' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-800 text-white'}`} data-id="element-2557">
                        {testimonial.tier} Member
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-2558">"{testimonial.quote}"</p>
                <div className="flex items-center justify-between text-sm" data-id="element-2559">
                  <div className="flex items-center text-yellow-500" data-id="element-2560">
                    <StarIcon size={16} className="fill-current" data-id="element-2561" />
                    <StarIcon size={16} className="fill-current" data-id="element-2562" />
                    <StarIcon size={16} className="fill-current" data-id="element-2563" />
                    <StarIcon size={16} className="fill-current" data-id="element-2564" />
                    <StarIcon size={16} className="fill-current" data-id="element-2565" />
                  </div>
                  <span className="text-gray-500" data-id="element-2566">
                    Recent trip: {testimonial.trip}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      {/* Exclusive Member Deals */}
      <section className="py-16" data-id="element-2567">
        <div className="container mx-auto px-4" data-id="element-2568">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-2569">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2570">
              Members-Only Offers
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2571">
              These exclusive deals are available only to EU Travel Club
              members.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2572">
            {exclusiveDeals.map(deal => <div key={deal.id} className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all ${deal.featured ? 'border-blue-300 relative' : 'border-gray-200'}`} data-id="element-2573">
                {deal.featured && <div className="absolute top-3 right-3 z-10" data-id="element-2574">
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-full" data-id="element-2575">
                      Featured Deal
                    </span>
                  </div>}
                <div className="relative aspect-[4/3] overflow-hidden" data-id="element-2576">
                  <img src={deal.image} alt={deal.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="element-2577" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4" data-id="element-2578">
                    <div className="text-white font-medium" data-id="element-2579">
                      {deal.destination}
                    </div>
                    <div className="text-white/80 text-sm" data-id="element-2580">{deal.duration}</div>
                  </div>
                </div>
                <div className="p-4" data-id="element-2581">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2582">
                    {deal.title}
                  </h3>
                  <div className="flex items-center justify-between mb-4" data-id="element-2583">
                    <div data-id="element-2584">
                      <div className="text-sm text-gray-500 line-through" data-id="element-2585">
                        CHF {deal.regularPrice}
                      </div>
                      <div className="text-xl font-bold text-gray-900" data-id="element-2586">
                        CHF {deal.memberPrice}
                      </div>
                    </div>
                    <div className="px-2 py-1 bg-green-100 text-green-800 text-sm font-medium rounded" data-id="element-2587">
                      {deal.discount}% OFF
                    </div>
                  </div>
                  <button className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors" data-id="element-2588">
                    View Details
                  </button>
                </div>
              </div>)}
          </div>
          <div className="text-center mt-8" data-id="element-2589">
            <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2590">
              View All Member Offers
              <ChevronRightIcon size={16} className="ml-1" data-id="element-2591" />
            </a>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-gray-50" data-id="element-2592">
        <div className="container mx-auto px-4" data-id="element-2593">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-2594">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2595">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600" data-id="element-2596">
              Find answers to common questions about EU Travel Club membership.
            </p>
          </div>
          <div className="max-w-3xl mx-auto" data-id="element-2597">
            {faqs.map((faq, index) => <div key={index} className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-2598">
                <button className="flex items-center justify-between w-full px-6 py-4 text-left bg-white hover:bg-gray-50" onClick={() => toggleFaq(index)} data-id="element-2599">
                  <h3 className="font-medium text-gray-900" data-id="element-2600">{faq.question}</h3>
                  {openFaq === index ? <ChevronUpIcon size={20} className="text-gray-500 flex-shrink-0" data-id="element-2601" /> : <ChevronDownIcon size={20} className="text-gray-500 flex-shrink-0" data-id="element-2602" />}
                </button>
                {openFaq === index && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-2603">
                    <p className="text-gray-700" data-id="element-2604">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>
        </div>
      </section>
      {/* Sign-Up CTA */}
      <section className="py-16 bg-blue-600" data-id="element-2605">
        <div className="container mx-auto px-4" data-id="element-2606">
          <div className="max-w-4xl mx-auto" data-id="element-2607">
            <div className="text-center mb-12" data-id="element-2608">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2609">
                Ready to Join?
              </h2>
              <p className="text-xl text-white/90" data-id="element-2610">
                Choose your membership level and start enjoying exclusive
                benefits today.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-id="element-2611">
              <div className="p-8" data-id="element-2612">
                <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2613">
                  Membership Selection
                </h3>
                <div className="space-y-4 mb-8" data-id="element-2614">
                  <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedTier === 'silver' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedTier('silver')} data-id="element-2615">
                    <div className="flex items-center" data-id="element-2616">
                      <div className={`w-5 h-5 rounded-full border flex-shrink-0 mr-3 flex items-center justify-center ${selectedTier === 'silver' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`} data-id="element-2617">
                        {selectedTier === 'silver' && <div className="w-2 h-2 bg-white rounded-full" data-id="element-2618"></div>}
                      </div>
                      <div className="flex-1" data-id="element-2619">
                        <div className="flex items-center justify-between" data-id="element-2620">
                          <div className="font-medium text-gray-900" data-id="element-2621">
                            Silver Membership
                          </div>
                          <div className="font-bold text-gray-900" data-id="element-2622">
                            RM 99/year
                          </div>
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-2623">
                          Basic benefits for occasional travelers
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedTier === 'gold' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedTier('gold')} data-id="element-2624">
                    <div className="flex items-center" data-id="element-2625">
                      <div className={`w-5 h-5 rounded-full border flex-shrink-0 mr-3 flex items-center justify-center ${selectedTier === 'gold' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`} data-id="element-2626">
                        {selectedTier === 'gold' && <div className="w-2 h-2 bg-white rounded-full" data-id="element-2627"></div>}
                      </div>
                      <div className="flex-1" data-id="element-2628">
                        <div className="flex items-center justify-between" data-id="element-2629">
                          <div className="font-medium text-gray-900" data-id="element-2630">
                            Gold Membership
                          </div>
                          <div className="font-bold text-gray-900" data-id="element-2631">
                            RM 199/year
                          </div>
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-2632">
                          Enhanced benefits for regular travelers
                        </div>
                        <div className="mt-1 inline-block px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded" data-id="element-2633">
                          Most Popular
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedTier === 'platinum' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedTier('platinum')} data-id="element-2634">
                    <div className="flex items-center" data-id="element-2635">
                      <div className={`w-5 h-5 rounded-full border flex-shrink-0 mr-3 flex items-center justify-center ${selectedTier === 'platinum' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`} data-id="element-2636">
                        {selectedTier === 'platinum' && <div className="w-2 h-2 bg-white rounded-full" data-id="element-2637"></div>}
                      </div>
                      <div className="flex-1" data-id="element-2638">
                        <div className="flex items-center justify-between" data-id="element-2639">
                          <div className="font-medium text-gray-900" data-id="element-2640">
                            Platinum Membership
                          </div>
                          <div className="font-bold text-gray-900" data-id="element-2641">
                            RM 399/year
                          </div>
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-2642">
                          Premium benefits for frequent travelers
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2643">
                  Your Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-2644">
                  <div data-id="element-2645">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2646">
                      First Name
                    </label>
                    <input type="text" id="firstName" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2647" />
                  </div>
                  <div data-id="element-2648">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2649">
                      Last Name
                    </label>
                    <input type="text" id="lastName" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2650" />
                  </div>
                  <div data-id="element-2651">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2652">
                      Email Address
                    </label>
                    <input type="email" id="email" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2653" />
                  </div>
                  <div data-id="element-2654">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2655">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2656" />
                  </div>
                </div>
                <div className="mb-6" data-id="element-2657">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2658">
                    Travel Preferences (Optional)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-2659">
                    <label className="flex items-center p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50" data-id="element-2660">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2661" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-2662">
                        Adventure
                      </span>
                    </label>
                    <label className="flex items-center p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50" data-id="element-2663">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2664" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-2665">
                        Cultural
                      </span>
                    </label>
                    <label className="flex items-center p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50" data-id="element-2666">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2667" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-2668">Luxury</span>
                    </label>
                    <label className="flex items-center p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50" data-id="element-2669">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2670" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-2671">Family</span>
                    </label>
                  </div>
                </div>
                <div className="mb-6" data-id="element-2672">
                  <h4 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2673">
                    Payment Details
                  </h4>
                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50" data-id="element-2674">
                    <div className="flex items-center mb-4" data-id="element-2675">
                      <CreditCardIcon size={20} className="text-gray-500 mr-2" data-id="element-2676" />
                      <span className="text-gray-700 font-medium" data-id="element-2677">
                        Secure Payment
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2678">
                      <div className="md:col-span-2" data-id="element-2679">
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2680">
                          Card Number
                        </label>
                        <input type="text" id="cardNumber" placeholder="1234 5678 9012 3456" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2681" />
                      </div>
                      <div data-id="element-2682">
                        <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2683">
                          Expiry Date
                        </label>
                        <input type="text" id="expiry" placeholder="MM/YY" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2684" />
                      </div>
                      <div data-id="element-2685">
                        <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2686">
                          CVV
                        </label>
                        <input type="text" id="cvv" placeholder="123" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2687" />
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-2688">
                      <input type="checkbox" id="saveCard" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2689" />
                      <label htmlFor="saveCard" className="ml-2 text-sm text-gray-700" data-id="element-2690">
                        Save card for future payments
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mb-8" data-id="element-2691">
                  <div className="flex items-center" data-id="element-2692">
                    <input type="checkbox" id="terms" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2693" />
                    <label htmlFor="terms" className="ml-2 text-sm text-gray-700" data-id="element-2694">
                      I agree to the{' '}
                      <a href="#" className="text-blue-600 hover:underline" data-id="element-2695">
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-blue-600 hover:underline" data-id="element-2696">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>
                <button className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" data-id="element-2697">
                  Complete Membership Registration
                </button>
                <div className="mt-4 text-center text-sm text-gray-600" data-id="element-2698">
                  Your membership will begin immediately after payment
                  processing
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer data-id="element-2699" />
    </div>;
}